import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { brandKeys } from '@/features/settings/use-brand'
import {
  addWebsiteDomainVoiceSource,
  consolidateVoice,
  deleteVoiceSource,
  listVoiceSources,
  reanalyzeVoiceSource,
  updateVoiceAnalysis,
  uploadVoiceSource,
  type VoiceAnalysisUpdate,
  type VoiceSourceType,
} from './api'

export const voiceSourceKeys = {
  all: ['brand-voice-sources'] as const,
  list: (brandId: string) => [...voiceSourceKeys.all, 'list', brandId] as const,
}

export function useVoiceSources(
  brandId: string | null | undefined,
  options?: { activePolling?: boolean },
) {
  return useQuery({
    queryKey: voiceSourceKeys.list(brandId ?? ''),
    queryFn: () => listVoiceSources(brandId!),
    enabled: !!brandId,
    staleTime: options?.activePolling ? 0 : 15_000,
    refetchInterval: options?.activePolling ? 3_000 : false,
  })
}

export function useUploadVoiceSource(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      sourceType,
      sourceName,
      skipAnalyze,
    }: {
      file: File
      sourceType: VoiceSourceType
      sourceName?: string
      skipAnalyze?: boolean
    }) => uploadVoiceSource(brandId, file, sourceType, sourceName, { skipAnalyze }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceSourceKeys.list(brandId) })
    },
  })
}

export function useAddWebsiteDomainVoiceSource(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { url: string; depth?: number; skipAnalyze?: boolean }) =>
      addWebsiteDomainVoiceSource(brandId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceSourceKeys.list(brandId) })
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useReanalyzeVoiceSource(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sourceId: string) => reanalyzeVoiceSource(brandId, sourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceSourceKeys.list(brandId) })
    },
  })
}

export function useConsolidateVoice(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => consolidateVoice(brandId),
    onSuccess: () => {
      // Brand-Profil-Liste invalidieren, damit voice_analysis im Editor sichtbar wird.
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}

export function useDeleteVoiceSource(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sourceId: string) => deleteVoiceSource(brandId, sourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceSourceKeys.list(brandId) })
    },
  })
}

export function useUpdateVoiceAnalysis(brandId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (update: VoiceAnalysisUpdate) => updateVoiceAnalysis(brandId, update),
    onSuccess: () => {
      // Brand-Profil-Liste neu laden, damit voice_analysis im Editor sichtbar ist.
      void qc.invalidateQueries({ queryKey: brandKeys.profiles() })
    },
  })
}
